import React, { useState } from 'react';

import {
  type CollectionReference,
  type DocumentData,
  type FirestoreError,
  getDocs,
  onSnapshot,
  type Query,
} from 'firebase/firestore';

import errorHandler from '@server/logger/errorHandler';

/**
 *
 * @param collectionRef CollectionReference | Query - Can be either a collection reference or a query.
 * The main difference is that the reference will return all documents in a collection,
 * while with the query you can specify conditionals, limits and order of fetched items
 * @param realtime Boolean (default is set to true) - If this is a one time data fetch, pass realtime prop as false.
 * @returns  3 variables:
 * data -> a collection of documents as an any[] of objects.
 * error -> returns a firestoreError if there's one.
 * loading -> returns the loading state, it will be true while data is being fetched and false when data is fetched or when an error occurs.
 */
const useCollectionDataFetch = <T extends DocumentData>(
  collectionRef: CollectionReference<T> | Query<T>,
  realtime?: boolean,
) => {
  const [data, setData] = useState<T[]>([]);
  const [error, setError] = useState<FirestoreError | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  React.useEffect(() => {
    setLoading(true);
    const getDocuments = async () => {
      if (realtime === true || realtime === undefined) {
        // Realtime updates the UI
        const unsubscribe = onSnapshot(
          collectionRef,
          QuerySnapshot => {
            const documentsArray: T[] = [];
            QuerySnapshot.forEach(doc => {
              const data = doc.data();
              const id = doc.id;
              if (id !== null && id !== 'null') {
                documentsArray.push({ ...data, id });
              }
            });
            setData(documentsArray);
          },
          error => {
            setError(error);
            errorHandler({ error });
          },
        );
        setLoading(false);
        return () => {
          unsubscribe();
        };
      } else {
        // One time fetch (no firestore listener)
        try {
          const querySnapshot = await getDocs(collectionRef);
          const documentsArray: T[] = [];
          if (!querySnapshot.empty) {
            querySnapshot.forEach(doc => {
              if (doc.id !== null && doc.id !== 'null') {
                documentsArray.push({ ...doc.data(), id: doc.id });
              }
            });
            setData(documentsArray);
          }
        } catch (error: any) {
          setError(error);
          errorHandler({ error });
        }
        setLoading(false);
      }
    };
    getDocuments();
  }, []);
  return { data, error, loading };
};
export default useCollectionDataFetch;
